import React from 'react';
import type { Theme } from '@material-ui/core';
import type { TypographyProps } from '@material-ui/core/Typography';
import classnames from 'classnames';

import type { IKlpStyles } from '@klp/design/Theme';
import { useKlpStyles } from '@klp/design/Theme';

import { Typography } from '../Typography';

import type { IconName, PictogramNameType } from './assets/IconNames';
import SvgUrlIcons from './assets/klp-icons.svg';
import SvgUrlPictograms from './assets/klp-pictograms.svg';
import { IconScale } from './IconScale';
import type { IconSize } from './IconSize';

interface IOwnProps {
    size?: IconSize;
    icon?: IconName;
    pictogram?: PictogramNameType;
    disabled?: boolean;
}

const styles = (props: IIllustrationProps) => (theme: Theme) => ({
    svgWrapper: {
        width: IconScale[props.size || 'base'].width,
        height: IconScale[props.size || 'base'].height,
        display: 'inline-block',
        whiteSpace: 'nowrap',
        wordWrap: 'normal',
    },
    svg: {
        maxWidth: '100%',
        maxHeight: '100%',
        width: '100%',
        height: '100%',
    },
    svgEnabled: {
        fill: theme.palette[props.klpPalette || 'primary']![
            props.klpColor || 'main'
        ],
    },
    svgDisabled: {
        fill: theme.palette['primary']['medium']?.concat(' !important'),
    },
});

export type IIllustrationProps = IOwnProps & IKlpStyles & TypographyProps;

export const Illustration = React.forwardRef<
    HTMLSpanElement,
    IIllustrationProps
>((props, ref) => {
    const classes = useKlpStyles(props, styles(props));

    const { icon, pictogram, disabled, className, ...restProps } = props;

    if (!icon && !pictogram) return null;

    const svgUrl = icon ? SvgUrlIcons : SvgUrlPictograms;

    return (
        <Typography
            ref={ref}
            data-testid="Illustration-IconWrapper"
            className={`${classes.svgWrapper} ${className}`}
            {...restProps}
        >
            <svg
                className={classnames(classes.svg, {
                    [classes.svgDisabled]: disabled,
                    [classes.svgEnabled]: !disabled,
                })}
            >
                <use
                    href={`${svgUrl}#${icon || pictogram}`}
                    data-testid={`${icon ? 'icon' : 'pictogram'}-${icon || pictogram}`}
                />
            </svg>
        </Typography>
    );
});

Illustration.displayName = 'Illustration';
